import { GenericDialog } from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { Button, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { showClearValuesDialog, continueWithClearValues } from '../state/atoms';
import { RiskFieldsTypeEnum } from '@/__generated__/graphql';

export const ClearValuesDialog = ({ selectedTab }: { selectedTab: string }) => {
  const [open, setOpen] = useAtom(showClearValuesDialog);
  const setContinueWithClearValues = useSetAtom(continueWithClearValues);

  const handleCancel = useCallback(() => {
    setOpen(false);
    setContinueWithClearValues(false);
  }, [setOpen, setContinueWithClearValues]);

  const handleContinue = useCallback(() => {
    setOpen(false);
    setContinueWithClearValues(true);
  }, [setOpen, setContinueWithClearValues]);

  return (
    <GenericDialog
      open={open}
      dialogStyles={{
        '.MuiDialog-container': {
          alignItems: 'baseline',
          paddingTop: '14.8rem',
        },
      }}
      showCloseIcon
      paperStyles={{
        maxWidth: '47.2rem',
      }}
      titleStyles={{
        fontSize: '2rem',
        fontWeight: 500,
        letterSpacing: -0.5,
        lineHeight: '2.4rem',
        padding: '2.4rem 2.4rem 0.8rem 2.4rem',
        color: 'primary.500',
      }}
      onClose={() => setOpen(false)}
      title="Recalculate legacy values?"
      contentStyles={{
        padding: '2.4rem',
      }}
      content={
        <Stack gap="1.6rem">
          <Typography variant="h6" fontWeight={400} color="primary.500">
            To recalculate legacy values, you must first delete the existing calculated results.
          </Typography>
          <Typography variant="h6" fontWeight={400} color="primary.500">
            This will permanently remove:
            <ul style={{ paddingLeft: '3.2rem', margin: 0, marginTop: '0.4rem' }}>
              {selectedTab === RiskFieldsTypeEnum.Pof && <li>POF values</li>}
              {selectedTab === RiskFieldsTypeEnum.Cof && <li>COF values</li>}
              {selectedTab === RiskFieldsTypeEnum.TransitionPeriod && (
                <li>Transition period data</li>
              )}
              <li>Risk values</li>
            </ul>
          </Typography>
          <Typography variant="h6" fontWeight={400} color="primary.500">
            This action cannot be undone.
          </Typography>
        </Stack>
      }
      actions={
        <Stack
          gap="1.15rem"
          direction="row"
          sx={{
            '.MuiButton-root': {
              padding: '0.6rem 1.6rem',
              fontWeight: 500,
              lineHeight: '2.4rem',
              letterSpacing: '0.04rem',
              fontSize: '1.4rem',
            },
          }}
        >
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" sx={{ fontWeight: 500 }} onClick={handleContinue}>
            Delete & Recalculate
          </Button>
        </Stack>
      }
      actionsStyles={{
        padding: '1.6rem 2.4rem',
        borderTop: `0.1rem solid ${abyssColors.primary[100]}`,
      }}
    />
  );
};
